import AirIcon from "@mui/icons-material/Air";
import BoltIcon from "@mui/icons-material/Bolt";
import LocalDrinkIcon from "@mui/icons-material/LocalDrink";
import ThermostatIcon from "@mui/icons-material/Thermostat";
import { ComponentType } from "react";
import { SensorConfig } from "verdi-common";
import { dayMs, hourMs } from "verdi-common/timeConstants";
import TensiometerIcon from "@/icons/inline-react-svgs/dynamic-graph/tensiometer-icon";
import WatermarkIcon from "@/icons/inline-react-svgs/dynamic-graph/watermark-icon";
import PressureTransducerIcon from "@/icons/inline-react-svgs/pressure-transducer-icon";

type UnitCategory = SensorConfig.UnitCategory;

export type DynamicGraphUnitCategoryOptions = {
    unit: string;
    hardZero: boolean;
    interpolation: "square" | "linear";
    invert: boolean;
    typicalRange: [number, number];
    YPositionMultiplier: number;
    smoothingVarianceThreshold: number;
    conversionEquation?: number[];
    maxInterpolationTime: number;
    icon: ComponentType;
};

/**
 * For each unit category, how should we display the graph.
 */
export const graphOptionsByUnitCategory: Partial<Record<UnitCategory, DynamicGraphUnitCategoryOptions>> = {
    liquidFlow: {
        unit: "LPM",
        hardZero: true,
        interpolation: "square",
        invert: false,
        typicalRange: [0, 5],
        YPositionMultiplier: 1,
        smoothingVarianceThreshold: 1.2,
        maxInterpolationTime: dayMs,
        icon: AirIcon,
    },
    airTemperature: {
        unit: "°C",
        hardZero: true,
        interpolation: "linear",
        invert: false,
        typicalRange: [10, 45],
        YPositionMultiplier: 1,
        smoothingVarianceThreshold: 0,
        maxInterpolationTime: dayMs,
        icon: ThermostatIcon,
    },
    boardTemperature: {
        unit: "°C",
        hardZero: false,
        interpolation: "linear",
        invert: false,
        typicalRange: [10, 45],
        YPositionMultiplier: 1,
        smoothingVarianceThreshold: 0,
        maxInterpolationTime: dayMs,
        icon: ThermostatIcon,
    },
    plantTemperature: {
        unit: "°C",
        hardZero: true,
        interpolation: "linear",
        invert: false,
        typicalRange: [10, 45],
        YPositionMultiplier: 1,
        smoothingVarianceThreshold: 0,
        maxInterpolationTime: dayMs,
        icon: ThermostatIcon,
    },
    soilTemperature: {
        unit: "°C",
        hardZero: true,
        interpolation: "linear",
        invert: false,
        typicalRange: [10, 45],
        YPositionMultiplier: 1,
        smoothingVarianceThreshold: 0,
        maxInterpolationTime: dayMs,
        icon: ThermostatIcon,
    },
    liquidPressure: {
        unit: "PSI",
        hardZero: true,
        interpolation: "square",
        invert: false,
        typicalRange: [10, 45],
        YPositionMultiplier: 1,
        smoothingVarianceThreshold: 2,
        maxInterpolationTime: dayMs,
        conversionEquation: [0, 0.145038],
        icon: PressureTransducerIcon,
    },
    volumetricSoilMoisture: {
        unit: "%",
        hardZero: true,
        interpolation: "linear",
        invert: false,
        typicalRange: [0, 30],
        YPositionMultiplier: 1,
        smoothingVarianceThreshold: 1.0,
        maxInterpolationTime: dayMs,
        icon: WatermarkIcon,
    },
    soilTension: {
        unit: "KPa",
        hardZero: true,
        interpolation: "linear",
        invert: true,
        typicalRange: [0, 20],
        YPositionMultiplier: -1,
        smoothingVarianceThreshold: 1.5,
        maxInterpolationTime: dayMs,
        icon: TensiometerIcon,
    },
    soilElectricalConductivity: {
        unit: "μS/cm",
        hardZero: true,
        interpolation: "linear",
        invert: false,
        typicalRange: [100, 800],
        YPositionMultiplier: 1,
        smoothingVarianceThreshold: 1.5,
        maxInterpolationTime: dayMs,
        icon: BoltIcon,
    },
    precipitation: {
        unit: "mm",
        hardZero: true,
        interpolation: "linear",
        invert: false,
        typicalRange: [0, 30],
        YPositionMultiplier: 1,
        smoothingVarianceThreshold: 1.5,
        maxInterpolationTime: dayMs,
        conversionEquation: [0, 1000],
        icon: LocalDrinkIcon,
    },
    liquidTankLevel: {
        unit: "ft",
        hardZero: true,
        interpolation: "linear",
        invert: false,
        typicalRange: [0, 10],
        YPositionMultiplier: 1,
        smoothingVarianceThreshold: 1.5,
        maxInterpolationTime: dayMs,
        conversionEquation: [0, 3.28084],
        icon: LocalDrinkIcon,
    },
    electricalVoltage: {
        unit: "V",
        hardZero: true,
        interpolation: "linear",
        invert: false,
        typicalRange: [0, 5],
        YPositionMultiplier: 1,
        smoothingVarianceThreshold: 1.5,
        maxInterpolationTime: hourMs,
        icon: BoltIcon,
    },
};
